import { BRAND } from './brand';
import { DEFAULT_PRIMARY_CTA } from './shared-ctas';
import type { PageCopy } from './page-copy-types';

// ─── /blog (index) ────────────────────────────────────────────────────────
export const BLOG_INDEX_COPY: PageCopy = {
  eyebrow: 'BizRnR Journal',
  h1: 'AI automation insights',
  lede:
    'Field notes on AI voice automation, lead generation, and scaling small business with autonomous receptionists.',
  // canonical-divergence: blog index "Read article" CTA points to a single post, not the subscription CTA.
  ctaPrimary: { label: 'Read article →', href: '/blog' },
  ctaSecondary: { label: 'Subscribe to RSS', href: '/blog/feed.xml' },
  sections: [
    {
      id: 'featured',
      eyebrow: 'Featured',
      heading: 'Latest from the journal',
      body:
        'The most recent piece — long-form fieldwork on how autonomous AI receptionists are reshaping inbound business reception.',
    },
    {
      id: 'archive',
      eyebrow: 'Archive',
      heading: 'Every post in the BizRnR journal',
      body:
        'Practical guides, frameworks, and reports — from missed-call cost models to AI Business Page playbooks.',
    },
    {
      id: 'empty',
      heading: 'New journal entries soon',
      body: 'Long-form pieces ship on a regular cadence. Subscribe via RSS to be notified when the next entry goes live.',
    },
  ],
  metadata: {
    title: 'BizRnR Journal — AI Voice Automation Insights & Reports',
    description:
      'Long-form articles, guides, and reports on AI voice automation, lead generation, and scaling small business with BizRnR.',
    canonical: 'https://bizrnr.com/blog',
  },
};

// ─── /blog/[slug] (dynamic shell) ─────────────────────────────────────────
// dynamic content lives in the `blog_posts` Supabase table (post.title,
// post.excerpt, post.content markdown body, post.meta_title, post.meta_description)
export const BLOG_SLUG_COPY: PageCopy = {
  eyebrow: 'BizRnR Journal',
  h1: '{post.title}',
  lede: '{post.excerpt}',
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  ctaSecondary: { label: 'Read more articles', href: '/blog' },
  sections: [
    {
      id: 'related-industries',
      eyebrow: 'Topic cluster',
      heading: 'BizRnR for your industry',
      body: 'Fieldwork-tested AI voice automation playbooks tailored for the verticals BizRnR serves today.',
    },
    {
      id: 'final-cta',
      eyebrow: 'Ready to ship',
      heading: 'Automate every inbound business call.',
      body: `Deploy your AI receptionist in under ${BRAND.setupTimeClaim}. 30-day guarantee. Cancel anytime.`,
    },
    {
      id: 'back',
      heading: 'Back to the journal',
    },
  ],
  metadata: {
    title: '{post.meta_title || post.title}',
    description: '{post.meta_description || post.excerpt}',
    canonical: 'https://bizrnr.com/blog/{slug}',
  },
};

// ─── /faq ─────────────────────────────────────────────────────────────────
//
// Sections double as topic groups. Each section has an id that maps the FAQ
// `topic` field below to a sticky-nav link on /faq. Wave 2 V4 canonical
// rebuild — see src/app/faq/page.tsx and src/components/faq/**.
export const FAQ_COPY: PageCopy = {
  eyebrow: 'Frequently asked',
  h1: 'Answers about your AI voice agent.',
  lede:
    'Everything you need to know about BizRnR — what it does, what it costs, how fast it goes live, and how it fits beside the team you already have.',
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  ctaSecondary: { label: 'Talk to Bri', href: '/' },
  sections: [
    { id: 'product', eyebrow: 'Product', heading: 'About BizRnR' },
    { id: 'pricing', eyebrow: 'Pricing', heading: 'Pricing & plans' },
    { id: 'setup', eyebrow: 'Setup', heading: 'Setup & launch' },
    { id: 'integrations', eyebrow: 'Integrations', heading: 'CRM & integrations' },
    { id: 'fit', eyebrow: 'Fit', heading: 'Industries & fit' },
    { id: 'closing', heading: 'Still have questions? We are here to help.' },
  ],
  faqs: [
    { topic: 'product', q: 'What is BizRnR?', a: 'BizRnR is an AI voice automation platform that deploys an AI employee to call, qualify, and convert your leads 24/7 — without any human intervention. It handles inbound and outbound calls, books appointments, and updates your CRM automatically.' },
    { topic: 'product', q: 'How does BizRnR AI voice agent work?', a: 'BizRnR uses ElevenLabs voice AI to conduct natural phone conversations. The agent calls leads, qualifies them with intelligent questions, books appointments directly into your calendar, and updates your CRM — all without human involvement.' },
    { topic: 'product', q: 'Can BizRnR replace my receptionist?', a: "Yes. BizRnR's AI voice agent answers every call 24/7, qualifies prospects, books appointments, and follows up automatically — handling everything a receptionist does, but autonomously and at scale." },
    { topic: 'product', q: 'Is BizRnR available 24/7?', a: "Yes. BizRnR's AI voice agents operate 24 hours a day, 7 days a week — including holidays. Your business never misses a lead, even outside business hours." },
    { topic: 'product', q: 'What makes BizRnR different from other AI voice tools?', a: 'BizRnR is a complete autonomous revenue system — not just a voice bot. It combines AI phone calls, lead qualification, appointment booking, email outreach, SMS follow-up, and a full CRM pipeline in one platform, purpose-built for small business conversion.' },
    { topic: 'pricing', q: 'How much does BizRnR cost?', a: 'BizRnR AI Receptionist is $99/month, with cancel-anytime billing and a 30-day money-back guarantee. For multi-location, white-label, API workflows, or call center replacement, Enterprise pricing is custom — talk to us about what you need.' },
    { topic: 'pricing', q: 'Do I have to commit to a long contract?', a: 'No. Start AI Receptionist at $99/month. You can cancel anytime, and the first 30 days are backed by a money-back guarantee.' },
    { topic: 'setup', q: 'How quickly can I deploy my AI voice agent?', a: `Your AI voice agent can be live in under ${BRAND.setupTimeClaim} — no technical setup required. Sign up, configure your agent, and it's immediately ready to take and make calls.` },
    { topic: 'integrations', q: 'Does BizRnR integrate with my existing CRM?', a: 'BizRnR includes a built-in CRM powered by Supabase that tracks all leads, call activity, and pipeline stages. It also integrates with Instantly.ai for email outreach sequences and Twilio for SMS follow-up.' },
    { topic: 'fit', q: 'What industries does BizRnR work for?', a: 'BizRnR works for any business that handles phone-based lead conversion, including Real Estate, HVAC, Insurance, Medical practices, Legal services, Retail, Solar, Roofing, and more.' },
  ],
  metadata: {
    title: 'FAQ — BizRnR AI Voice Automation',
    description:
      'Frequently asked questions about BizRnR AI voice automation — how it works, pricing, setup time, CRM integrations, and more.',
    canonical: 'https://bizrnr.com/faq',
  },
};
